import { cn } from "@/lib/cn"
import type { FeedbackBarSegment } from "@/types/user_feedback"

// Hand-rolled horizontal stacked bar — one rounded row of colored divs sized
// by `pct`, with category labels + counts laid out beneath. Used at the top
// of the User Feedback page so an operator sees the bug / recommendation /
// unresolved mix at a glance without a chart lib.

type Props = { segments: FeedbackBarSegment[] }

// Tone-by-key uses Tailwind tokens from the existing palette so it matches
// the rest of the portal. Anything unmapped falls back to the muted neutral.
const TONE: Record<string, string> = {
  bug:            "bg-status-error",
  recommendation: "bg-vip-gold",
  unresolved:     "bg-status-warning",
  general:        "bg-app-muted",
  rating:         "bg-status-success",
  bad_behavior:   "bg-brand-red",
}

function toneFor(key: string): string {
  return TONE[key] ?? "bg-app-muted"
}

export function FeedbackStackedBar({ segments }: Props) {
  const total = segments.reduce((sum, s) => sum + s.count, 0)
  if (total === 0) {
    return (
      <div className="text-sm text-app-muted">No feedback recorded yet.</div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex h-3 w-full overflow-hidden rounded-chip bg-white/5">
        {segments.map((s) => (
          <div
            key={s.key}
            className={cn("h-full", toneFor(s.key))}
            style={{ width: `${s.pct}%` }}
            title={`${s.label}: ${s.count} (${s.pct.toFixed(1)}%)`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs">
        {segments.map((s) => (
          <div key={s.key} className="flex items-center gap-2 text-app-muted">
            <span className={cn("h-2 w-2 rounded-full", toneFor(s.key))} />
            <span className="text-app-text">{s.label}</span>
            <span className="text-app-subtle">{s.count}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
